import React, { useState, useRef, useEffect } from "react";
import styles from "./VoiceInput.module.css";
import type { VoiceInputProps, SpeechRecognition, SpeechRecognitionEvent } from "./types";
import { apiService } from "../../services/apiService";
import { socketService } from "../../services/socketService";
import { MicrophoneIcon, StopIcon, MicrophoneOffIcon } from "./icons";
import { resolveRecognitionLocale } from "./recognitionLocale";
import { useUiCopy } from "../../i18n/useUiCopy";

const MAX_RECORDING_DURATION = 2 * 60 * 1000; // 2 minutes in milliseconds
const MAX_TEXT_LENGTH = 5000; // Maximum characters to prevent API issues

/**
 * VoiceInput Component - Optimized Speech-to-Text Interface
 *
 * Features:
 * - Continuous speech recognition until manually stopped
 * - Session-aware punctuation processing with backend integration
 * - Text accumulation and deduplication prevention
 * - Real-time state feedback and error handling
 * - Full accessibility support (ARIA attributes)
 * - Cross-browser compatibility with graceful degradation
 * - Automatic recording limits to prevent errors:
 *   • 2-minute maximum recording duration
 *   • 5,000 character text limit
 *   • Auto-stop on limits reached
 *   • Warning notifications for long recordings
 */

export const VoiceInput: React.FC<VoiceInputProps> = ({
  onTextReplace,
  currentText = "",
  disabled = false,
  className = "",
  recognitionLanguage = "English",
  onStateChange,
  onStreamInitialize,
}) => {
  const copy = useUiCopy();
  // Component state
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessingPunctuation, setIsProcessingPunctuation] = useState(false);

  // Refs for speech recognition and text management
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const accumulatedTextRef = useRef<string>(""); // Accumulates speech text during session
  const existingTextRef = useRef<string>(""); // Preserves existing text when starting
  const lastProcessedIndexRef = useRef<number>(0); // Prevents duplicate text processing
  const recordingStartTimeRef = useRef<number>(0); // Track recording duration
  const maxRecordingTimeRef = useRef<NodeJS.Timeout | null>(null); // Auto-stop timeout

  // Initialize speech recognition support and instance
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const isRecognitionSupported = !!SpeechRecognition;

    setIsSupported(isRecognitionSupported);

    if (isRecognitionSupported) {
      recognitionRef.current = new SpeechRecognition();
    }
  }, []);

  // Notify parent component of state changes
  useEffect(() => {
    onStateChange?.({ isListening, isProcessingPunctuation });
  }, [isListening, isProcessingPunctuation, onStateChange]);

  // Auto-stop timer management - separate from main speech recognition effect
  useEffect(() => {
    if (isListening) {
      recordingStartTimeRef.current = Date.now();

      maxRecordingTimeRef.current = setTimeout(() => {
        // Use the ref to get the current recognition instance
        if (recognitionRef.current && recordingStartTimeRef.current > 0) {
          console.warn("Auto-stopping voice recording after maximum duration");
          try {
            recognitionRef.current.stop();
          } catch (err) {
            console.error("Error auto-stopping recognition:", err);
            // Force cleanup if stop() fails
            setIsListening(false);
            recordingStartTimeRef.current = 0;
          }
        }
      }, MAX_RECORDING_DURATION);
    } else {
      // Clear timer when not listening
      if (maxRecordingTimeRef.current) {
        clearTimeout(maxRecordingTimeRef.current);
        maxRecordingTimeRef.current = null;
      }
      recordingStartTimeRef.current = 0;
    }

    // Cleanup on unmount or when isListening changes
    return () => {
      if (maxRecordingTimeRef.current) {
        clearTimeout(maxRecordingTimeRef.current);
        maxRecordingTimeRef.current = null;
      }
    };
  }, [isListening]);

  // Configure and setup speech recognition event handlers
  useEffect(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;

    // Process accumulated text with punctuation via backend service
    const processPunctuation = async (text: string): Promise<string> => {
      try {
        setIsProcessingPunctuation(true);
        const clientId = socketService.getId();
        const response = await apiService.addPunctuation(text, clientId);

        if (response.success) {
          return response.punctuated_text;
        } else {
          console.warn("Punctuation service returned error:", response.message);
          return text;
        }
      } catch (error) {
        console.error("Punctuation processing failed:", error);
        return text;
      } finally {
        setIsProcessingPunctuation(false);
      }
    };

    // Configure recognition settings
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = resolveRecognitionLocale(recognitionLanguage);

    // Handle recognition start
    const handleStart = () => {
      setIsListening(true);
      setError(null);
      accumulatedTextRef.current = "";
      existingTextRef.current = currentText;
      lastProcessedIndexRef.current = 0;
    };

    // Handle recognition end and process accumulated text
    const handleEnd = () => {
      setIsListening(false); // This will trigger the timer cleanup in the separate useEffect
      const fullText = accumulatedTextRef.current.trim();

      if (fullText) {
        // Check if text length is within reasonable limits
        if (fullText.length > MAX_TEXT_LENGTH) {
          const truncatedText = fullText.substring(0, MAX_TEXT_LENGTH);
          setError("Recording was very long and has been truncated");

          processPunctuation(truncatedText).then((punctuatedText) => {
            const existingText = existingTextRef.current.trim();
            const finalText = existingText ? `${existingText} ${punctuatedText}` : punctuatedText;
            onTextReplace?.(finalText);
          });
        } else {
          processPunctuation(fullText).then((punctuatedText) => {
            const existingText = existingTextRef.current.trim();
            const finalText = existingText ? `${existingText} ${punctuatedText}` : punctuatedText;
            onTextReplace?.(finalText);
          });
        }
      }
    };

    // Handle recognition errors
    const handleError = (event: any) => {
      setError(`Speech recognition error: ${event.error}`);
      setIsListening(false); // This will trigger the timer cleanup in the separate useEffect

      accumulatedTextRef.current = "";
      existingTextRef.current = "";
      lastProcessedIndexRef.current = 0;
    };

    // Handle recognition results with deduplication
    const handleResult = (event: Event) => {
      const speechEvent = event as SpeechRecognitionEvent;
      let finalTranscript = "";

      // Process only new results to prevent duplication
      for (let i = lastProcessedIndexRef.current; i < speechEvent.results.length; i++) {
        const result = speechEvent.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
          lastProcessedIndexRef.current = i + 1;
        }
      }

      // Accumulate final text for later processing
      if (finalTranscript.trim()) {
        const rawText = finalTranscript.trim();
        const currentAccumulated = accumulatedTextRef.current;
        const newAccumulated = currentAccumulated ? `${currentAccumulated} ${rawText}` : rawText;

        // Check if accumulated text is getting too long
        if (newAccumulated.length > MAX_TEXT_LENGTH) {
          console.warn("Voice input approaching maximum text length, auto-stopping");

          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
          return;
        }

        accumulatedTextRef.current = newAccumulated;
      }
    };

    // Attach event listeners
    recognition.addEventListener("start", handleStart);
    recognition.addEventListener("end", handleEnd);
    recognition.addEventListener("error", handleError);
    recognition.addEventListener("result", handleResult);

    // Cleanup event listeners on unmount
    return () => {
      recognition.removeEventListener("start", handleStart);
      recognition.removeEventListener("end", handleEnd);
      recognition.removeEventListener("error", handleError);
      recognition.removeEventListener("result", handleResult);
    };
  }, [onTextReplace, currentText, recognitionLanguage]);

  // Control functions for starting and stopping recognition
  const startListening = async () => {
    if (!recognitionRef.current || disabled) return;

    setError(null);
    accumulatedTextRef.current = "";
    existingTextRef.current = currentText;
    lastProcessedIndexRef.current = 0;

    // Initialize shared audio stream for audio visualization
    if (onStreamInitialize) {
      try {
        await onStreamInitialize();
      } catch (err) {
        console.error("Failed to initialize shared audio stream:", err);
        // Continue with voice recognition even if audio stream fails
      }
    }

    try {
      recognitionRef.current.start();
    } catch (err) {
      setError("Failed to start speech recognition");
      console.error("Speech recognition start error:", err);
    }
  };

  const stopListening = () => {
    if (!recognitionRef.current) return;

    try {
      recognitionRef.current.stop();
    } catch (err) {
      console.error("Speech recognition stop error:", err);
      // Force state cleanup if stop() fails
      setIsListening(false);
    }
  };

  const handleMicrophoneClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current && isListening) {
        recognitionRef.current.abort();
      }
    };
  }, [isListening]);

  if (process.env.REACT_APP_VOICE_ENABLED?.toLowerCase() !== "true") {
    return null;
  }

  // Early return for unsupported browsers
  if (!isSupported) {
    return (
      <div className={`${styles.container} ${className}`}>
        <button
          className={`${styles.button} ${styles.unsupported}`}
          title={copy.voiceUnsupportedTitle}
          disabled
          aria-label={copy.voiceUnsupported}
        >
          <MicrophoneOffIcon />
        </button>
      </div>
    );
  }

  // Determine button state and styling
  const isButtonDisabled = disabled || isProcessingPunctuation;
  const buttonClassName = `${styles.button} ${isListening ? styles.listening : ""} ${
    isProcessingPunctuation ? styles.processing : ""
  }`;

  const buttonTitle = isProcessingPunctuation
    ? copy.processingSpeech
    : isListening
    ? copy.clickToStopRecording
    : copy.clickToStartVoice;

  const buttonAriaLabel = isListening ? copy.stopVoice : copy.startVoice;

  return (
    <div className={`${styles.container} ${className}`}>
      <button
        aria-label={buttonAriaLabel}
        onClick={handleMicrophoneClick}
        disabled={isButtonDisabled}
        className={buttonClassName}
        title={buttonTitle}
        type="button"
      >
        {isListening ? <StopIcon /> : <MicrophoneIcon />}
      </button>

      {error && (
        <div className={styles.errorIndicator} title={error}>
          ⚠️
        </div>
      )}
    </div>
  );
};
